import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { getOfflineQueueTelemetry, type OfflineQueueTelemetry } from '../services/apiService.ts';

// P-OFF-22 — cross-tenant view of CLIENT offline-queue telemetry for the internal :3100 console.
//
// The offline write queue lives on each device (IndexedDB); the server never sees it directly. Client
// devices fire tiny beacons to /api/v1/sync/telemetry at the end of a drain pass / when a write is
// queued, and the backend aggregates the LAST snapshot per (tenant, device) in a bounded in-memory
// ring buffer (interim, schema-less — lost on restart). This page answers the L1 support question
// "the client says their write didn't arrive — is their queue stuck?" with per-device depth, failures,
// auth-blocks and staleness. Read-only: draining a queue is a client-side action (OfflineBanner).

const AUTO_REFRESH_MS = 60_000;

const StatTile: React.FC<{ label: string; value: number | string; hint?: string; danger?: boolean }> = ({
  label,
  value,
  hint,
  danger,
}) => (
  <Paper variant="outlined" sx={{ p: 2, height: '100%', borderColor: danger ? 'warning.main' : undefined }}>
    <Typography variant="overline" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="h4" sx={{ mt: 0.5 }} color={danger ? 'warning.main' : undefined}>
      {value}
    </Typography>
    {hint ? (
      <Typography variant="caption" color="text.secondary">
        {hint}
      </Typography>
    ) : null}
  </Paper>
);

function formatAge(ageSeconds: number): string {
  if (!Number.isFinite(ageSeconds) || ageSeconds < 0) return '—';
  if (ageSeconds < 60) return `${Math.floor(ageSeconds)}s ago`;
  if (ageSeconds < 3600) return `${Math.floor(ageSeconds / 60)}m ago`;
  return `${Math.floor(ageSeconds / 3600)}h ago`;
}

const OfflineQueuesPage: React.FC = () => {
  const [data, setData] = useState<OfflineQueueTelemetry | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | null>(null);
  const mountedRef = useRef(true);

  const load = useCallback(async () => {
    setRefreshing(true);
    setError(null);
    try {
      const resp = await getOfflineQueueTelemetry();
      if (!mountedRef.current) return;
      setData(resp);
      setLastRefreshedAt(new Date());
    } catch (err: any) {
      if (!mountedRef.current) return;
      setError(err?.response?.data?.message || err?.message || 'Offline-queue telemetry could not be loaded.');
    } finally {
      if (mountedRef.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    void load();
    const timer = setInterval(() => {
      void load();
    }, AUTO_REFRESH_MS);
    return () => {
      mountedRef.current = false;
      clearInterval(timer);
    };
  }, [load]);

  if (loading) {
    return (
      <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  const totals = data?.totals;
  const tenants = data?.tenants ?? [];

  return (
    <Stack spacing={3}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
        <Stack spacing={1}>
          <Typography variant="overline" color="text.secondary">
            Superadmin / Offline
          </Typography>
          <Typography variant="h4">Offline Queues</Typography>
          <Typography color="text.secondary">
            Per-device depth of the client-side offline write queue (IndexedDB), reported via beacons.
            Read-only &mdash; draining a stuck queue is a device action (OfflineBanner &ldquo;Sincronizează
            acum&rdquo; / per-row &ldquo;Retry&rdquo;).
          </Typography>
        </Stack>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => void load()}
          disabled={refreshing}
          sx={{ flexShrink: 0 }}
        >
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </Button>
      </Stack>

      {error ? <Alert severity="error">{error}</Alert> : null}

      <Grid container spacing={2}>
        <Grid item xs={6} md={3}>
          <StatTile label="Tenants reporting" value={totals?.tenants ?? 0} hint="with ≥ 1 active device" />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatTile label="Devices" value={totals?.devices ?? 0} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatTile
            label="Queued writes"
            value={totals?.queueDepth ?? 0}
            hint={`${totals?.failed ?? 0} failed`}
            danger={(totals?.queueDepth ?? 0) > 0}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatTile
            label="Stale / auth-blocked"
            value={`${totals?.stale ?? 0} / ${totals?.authBlocked ?? 0}`}
            hint="stale = silent > 5 min"
            danger={(totals?.stale ?? 0) > 0 || (totals?.authBlocked ?? 0) > 0}
          />
        </Grid>
      </Grid>

      {tenants.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="body2" color="text.secondary">
            No device has reported an offline queue yet. Beacons appear here after a device queues a
            write or finishes a drain pass with activity. (Storage is in-memory and resets on a backend
            restart.)
          </Typography>
        </Paper>
      ) : (
        tenants.map((tenant) => (
          <Paper key={tenant.tenantId} sx={{ overflowX: 'auto' }}>
            <Box sx={{ p: 2, pb: 0, display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="h6">{tenant.tenantName}</Typography>
              <Typography variant="caption" color="text.secondary">
                #{tenant.tenantId}
              </Typography>
              <Box sx={{ flex: 1 }} />
              <Chip
                size="small"
                variant="outlined"
                color={tenant.queueDepth > 0 ? 'warning' : 'default'}
                label={`${tenant.deviceCount} device(s) · ${tenant.queueDepth} queued`}
              />
            </Box>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Device</TableCell>
                  <TableCell align="right">Queue depth</TableCell>
                  <TableCell align="right">Failed</TableCell>
                  <TableCell align="center">Auth</TableCell>
                  <TableCell align="right">Last seen</TableCell>
                  <TableCell align="center">Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tenant.devices.map((device) => (
                  <TableRow key={device.deviceLabel} hover>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {device.deviceLabel}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {device.queueDepth > 0 ? (
                        <Chip label={device.queueDepth} size="small" color="warning" variant="outlined" />
                      ) : (
                        0
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {device.failedCount > 0 ? (
                        <Chip label={device.failedCount} size="small" color="error" variant="outlined" />
                      ) : (
                        0
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {device.authBlocked ? (
                        <Chip label="re-login" size="small" color="error" />
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          ok
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="caption" color={device.stale ? 'warning.main' : 'text.secondary'}>
                        {formatAge(device.ageSeconds)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {device.stale ? (
                        <Chip label="STALE" size="small" color="warning" variant="outlined" />
                      ) : (
                        <Chip label="live" size="small" color="success" variant="outlined" />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        ))
      )}

      <Typography variant="caption" color="text.secondary">
        Source: in-memory beacon aggregation (bounded ring buffer, interim / no schema — resets on
        backend restart). Auto-refreshes every 60s
        {lastRefreshedAt ? ` · last updated ${lastRefreshedAt.toLocaleTimeString()}` : ''}. Support
        runbook: docs/ops/OFFLINE_QUEUE_SUPPORT_RUNBOOK.md.
      </Typography>
    </Stack>
  );
};

export default OfflineQueuesPage;
